import browser from "webextension-polyfill";
import * as ewe from "@eyeo/webext-ad-filtering-solution";
import { CommandEventType, CommandName, CommandVersion, DeleteEventType, maximumProcessableCommands, } from "./command-library.types";
import { getStartInfo, setStartInfo } from "./start-info";
import { isDeleteBehavior, setDeleteCommandHandler } from "./delete-commands";
import { recordEvent, recordGenericEvent } from "./event-recording";
import { checkLanguage } from "./language-check";
import { isValidDate } from "./param-validator";
import { start as startTelemetry, stop as stopTelemetry } from "./telemetry";
const knownCommandsList = Object.values(CommandName);
export const commandStorageKey = "ipm_commands";
const actorByCommandName = new Map();
const unexecutableCommands = new Map();
let isActive = false;
function isCommand(candidate) {
    return (typeof candidate === "object" &&
        candidate !== null &&
        "version" in candidate &&
        "ipm_id" in candidate &&
        "command_name" in candidate &&
        "expiry" in candidate);
}
export function isCommandMap(candidate) {
    return (typeof candidate === "object" &&
        candidate !== null &&
        Object.keys(candidate).every((key) => typeof key === "string") &&
        Object.values(candidate).every(isCommand));
}
export function setCommandActor(commandName, actor) {
    actorByCommandName.set(commandName, actor);
    retryExecuteCommands(commandName);
}
export function dismissCommand(ipmId) {
    const { prefs } = getStartInfo();
    const command = getCommand(ipmId);
    if (!command) {
        return;
    }
    const commandStorage = prefs.get(commandStorageKey);
    delete commandStorage[command.ipm_id];
    void prefs.set(commandStorageKey, commandStorage);
}
export function getBehavior(ipmId) {
    const command = getCommand(ipmId);
    if (!command) {
        return null;
    }
    const actor = actorByCommandName.get(command.command_name);
    if (!actor) {
        return null;
    }
    return actor.getBehavior(command);
}
export function getCommand(ipmId) {
    const { prefs } = getStartInfo();
    const commandStorage = prefs.get(commandStorageKey);
    return commandStorage[ipmId] || null;
}
export function getStoredCommandIds() {
    const { prefs } = getStartInfo();
    const commandStorage = prefs.get(commandStorageKey);
    if (!isCommandMap(commandStorage)) {
        return [];
    }
    return Object.keys(commandStorage);
}
export function getContent(ipmId) {
    const command = getCommand(ipmId);
    if (!command) {
        return null;
    }
    const actor = actorByCommandName.get(command.command_name);
    if (!actor) {
        return null;
    }
    return actor.getContent(command, command);
}
export function isCommandExpired(command) {
    if (typeof command.expiry === "undefined") {
        return true;
    }
    if (!isValidDate(command.expiry)) {
        return true;
    }
    const expiryDateOnly = new Date(command.expiry);
    const nowDateTime = new Date();
    if (nowDateTime >= expiryDateOnly) {
        return true;
    }
    return false;
}
function retryExecuteCommands(commandName) {
    for (const [command, isInitialization] of unexecutableCommands) {
        if (command.command_name !== commandName) {
            continue;
        }
        unexecutableCommands.delete(command);
        void executeIPMCommands([command], isInitialization);
    }
}
function storeCommands(commands) {
    const { prefs, logger } = getStartInfo();
    let commandStorageModified = false;
    const storage = prefs.get(commandStorageKey);
    for (const command of commands) {
        if (typeof storage[command.ipm_id] !== "undefined") {
            logger.debug("[ipm]: Campaign already stored:", command.ipm_id);
            continue;
        }
        storage[command.ipm_id] = command;
        commandStorageModified = true;
    }
    if (commandStorageModified) {
        void prefs.set(commandStorageKey, storage);
    }
}
export async function executeIPMCommands(commands, isInitialization = false) {
    if (!isActive) {
        return;
    }
    const { logger } = getStartInfo();
    const actorByExecutableCommand = new Map();
    if (commands.length > maximumProcessableCommands) {
        logger.reportAndLogError(new Error("[ipm]: Too many commands received."));
        recordGenericEvent("too_many_commands");
        return;
    }
    for (const command of commands) {
        if (!isCommand(command)) {
            logger.reportAndLogError(new Error("[ipm]: Invalid command received."));
            continue;
        }
        if (!knownCommandsList.includes(command.command_name)) {
            logger.reportAndLogError(new Error(`[ipm]: Unknown command name received: ${command.command_name}`));
            continue;
        }
        if (command.version !== CommandVersion[command.command_name]) {
            logger.reportAndLogError(new Error(`[ipm]: Command version mismatch for command "${command.command_name}". Requested version was ${command.version}, version present is ${CommandVersion[command.command_name]}`));
            if (isInitialization) {
                dismissCommand(command.ipm_id);
            }
            continue;
        }
        if (isCommandExpired(command)) {
            logger.reportAndLogError(new Error("[ipm]: Command has expired."));
            recordEvent(command.ipm_id, command.command_name, CommandEventType.expired);
            if (isInitialization) {
                dismissCommand(command.ipm_id);
            }
            continue;
        }
        const actor = actorByCommandName.get(command.command_name);
        if (!actor) {
            logger.debug("[ipm]: No actor found:", command.command_name);
            unexecutableCommands.set(command, isInitialization);
            continue;
        }
        if (!actor.isValidCommand(command)) {
            logger.reportAndLogError(new Error("[ipm]: Invalid parameters received."));
            continue;
        }
        if (!("attributes" in command)) {
            command.attributes = {
                received: Date.now(),
                language: browser.i18n.getUILanguage(),
            };
        }
        actorByExecutableCommand.set(command, actor);
    }
    const executableCommands = Array.from(actorByExecutableCommand.keys());
    if (!isInitialization && executableCommands.length > 0) {
        storeCommands(executableCommands);
    }
    const commandHandlingPromises = Array.from(actorByExecutableCommand).map(async ([executableCommand, actor]) => {
        await actor.handleCommand(executableCommand.ipm_id, isInitialization);
    });
    void Promise.all(commandHandlingPromises).then(() => {
        knownCommandsList
            .map((name) => actorByCommandName.get(name))
            .forEach((actor) => actor === null || actor === void 0 ? void 0 : actor.onCommandsProcessed());
    });
}
function registerDeleteEvent(ipmId, name) {
    recordEvent(ipmId, CommandName.deleteCommands, name);
}
async function handleDeleteCommand(ipmId) {
    const { logger } = getStartInfo();
    const command = getCommand(ipmId);
    if (!command) {
        return;
    }
    const behavior = getBehavior(ipmId);
    if (!isDeleteBehavior(behavior)) {
        logger.reportAndLogError(new Error("[delete-commands]: Invalid command behavior."));
        return;
    }
    void checkLanguage(ipmId);
    if (isCommandExpired(command)) {
        logger.reportAndLogError(new Error("[delete-commands]: Command has expired."));
        registerDeleteEvent(ipmId, CommandEventType.expired);
        dismissCommand(ipmId);
        return;
    }
    const { commandIds } = behavior;
    let success = true;
    for (const commandId of commandIds) {
        try {
            dismissCommand(commandId);
            if (getCommand(commandId) !== null) {
                throw new Error("Command was not successfully deleted.");
            }
        }
        catch (error) {
            success = false;
            logger.reportAndLogError(new Error(`[delete-commands]: Error trying to delete command with ID ${commandId}`));
        }
    }
    registerDeleteEvent(ipmId, success ? DeleteEventType.sucess : DeleteEventType.error);
    dismissCommand(ipmId);
}
async function resetIPMCommands() {
    const { prefs } = getStartInfo();
    await prefs.untilLoaded;
    const ipmCommandsReset = prefs.get("ipm_commands_second_reset_completed");
    if (!ipmCommandsReset) {
        await prefs.set(commandStorageKey, {});
        await prefs.set("ipm_commands_second_reset_completed", true);
    }
}
export async function clearCommands() {
    for (const actor of actorByCommandName.values()) {
        await actor.clearCommands();
    }
}
export async function start(startInfo) {
    setStartInfo(startInfo);
    isActive = true;
    const { prefs } = startInfo;
    await prefs.untilLoaded;
    await resetIPMCommands();
    ewe.experiments.onChanged.addListener(async () => {
        await resetIPMCommands();
    });
    setDeleteCommandHandler(handleDeleteCommand);
    const commandStorage = prefs.get(commandStorageKey);
    if (isCommandMap(commandStorage)) {
        const commands = Object.values(commandStorage);
        await executeIPMCommands(commands, true);
    }
    await startTelemetry();
}
export async function stop() {
    isActive = false;
    await stopTelemetry();
    await clearCommands();
}
